import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, Loader2, Shield } from "lucide-react";
import { CreatePipelinePermissionRequest } from "@/types/pipeline";
import { Role } from "@/types/roles";

interface AssignPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (permission: CreatePipelinePermissionRequest) => Promise<void>;
  pipelineId: string;
  pipelineName: string;
  roles: Role[];
  existingRoleIds?: string[];
  isLoading?: boolean;
}

export function AssignPermissionsModal({
  isOpen,
  onClose,
  onSave,
  pipelineId,
  pipelineName,
  roles,
  existingRoleIds = [],
  isLoading = false,
}: AssignPermissionsModalProps) {
  const [formData, setFormData] = useState<CreatePipelinePermissionRequest>({
    pipeline_id: pipelineId,
    role_id: "",
    can_view: true,
    can_edit: true,
    can_delete: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableRoles = roles.filter(
    (role) => !existingRoleIds.includes(role.id)
  );

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        pipeline_id: pipelineId,
        role_id: "",
        can_view: true,
        can_edit: true,
        can_delete: false,
      });
      setErrors({});
    } else {
      setFormData((prev) => ({ ...prev, pipeline_id: pipelineId }));
    }
  }, [isOpen, pipelineId]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.role_id) {
      newErrors.role_id = "Debe seleccionar un rol";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error al asignar permisos:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof CreatePipelinePermissionRequest,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl">
            <Shield className="h-6 w-6 mr-2 text-blue-600" />
            Asignar Permisos al Pipeline
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Pipeline Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              Pipeline: <span className="font-bold">{pipelineName}</span>
            </p>
          </div>

          {/* Seleccionar Rol */}
          <div className="space-y-2">
            <Label htmlFor="role" className="flex items-center">
              Seleccionar Rol
              <span className="text-red-500 ml-1">*</span>
            </Label>
            {availableRoles.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  Todos los roles ya tienen permisos asignados para este
                  pipeline.
                </p>
              </div>
            ) : (
              <>
                <Select
                  value={formData.role_id}
                  onValueChange={(value) => handleChange("role_id", value)}
                >
                  <SelectTrigger
                    className={errors.role_id ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Seleccione un rol..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role_id && (
                  <div className="flex items-center text-red-500 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.role_id}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold">Permisos</Label>

            <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
              <div className="space-y-0.5">
                <Label htmlFor="can_view" className="font-medium">
                  Ver
                </Label>
                <p className="text-sm text-slate-500">
                  Permitir visualizar este pipeline
                </p>
              </div>
              <Switch
                id="can_view"
                checked={formData.can_view}
                onCheckedChange={(checked) => handleChange("can_view", checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
              <div className="space-y-0.5">
                <Label htmlFor="can_edit" className="font-medium">
                  Editar
                </Label>
                <p className="text-sm text-slate-500">
                  Permitir modificar oportunidades en este pipeline
                </p>
              </div>
              <Switch
                id="can_edit"
                checked={formData.can_edit}
                onCheckedChange={(checked) => handleChange("can_edit", checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
              <div className="space-y-0.5">
                <Label htmlFor="can_delete" className="font-medium">
                  Eliminar
                </Label>
                <p className="text-sm text-slate-500">
                  Permitir eliminar oportunidades en este pipeline
                </p>
              </div>
              <Switch
                id="can_delete"
                checked={formData.can_delete}
                onCheckedChange={(checked) =>
                  handleChange("can_delete", checked)
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting || isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting || isLoading || availableRoles.length === 0
            }
            className="bg-slate-900 hover:bg-black"
          >
            {isSubmitting || isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Asignando...
              </>
            ) : (
              "Asignar Permisos"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
